import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import ProductCard from './ProductCard';

function useColumnsCount(containerRef) {
  const [columns, setColumns] = useState(() => {
    if (typeof window !== 'undefined') {
      const width = containerRef.current?.offsetWidth || window.innerWidth;
      // Фиксированные брейкпоинты как в старом CSS
      if (width >= 1100) return 5;
      if (width >= 720) return 4;
      if (width >= 520) return 3;
      return 2;
    }
    return 4; // десктоп по умолчанию
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      if (width >= 1100) setColumns(5);
      else if (width >= 720) setColumns(4);
      else if (width >= 520) setColumns(3);
      else setColumns(2);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef]);

  return columns;
}

function CatalogGrid({
  products,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}) {
  const containerRef = useRef(null);
  const columnsCount = useColumnsCount(containerRef);

  const isMobile = columnsCount <= 2;
  const columnGap = isMobile ? 8 : 12;
  const rowGap = isMobile ? 18 : 26;
  const padding = '0';

  const rows = useMemo(() => {
    const result = [];
    for (let i = 0; i < products.length; i += columnsCount) {
      result.push(products.slice(i, i + columnsCount));
    }
    return result;
  }, [products, columnsCount]);

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    gap: rowGap,
    estimateSize: useCallback(() => 380, []),
    overscan: 5,
    measureElement: useCallback((el) => {
      return el.getBoundingClientRect().height;
    }, []),
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];
    if (
      lastItem &&
      lastItem.index >= rows.length - 5 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    virtualItems,
    rows.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          position: 'relative',
          width: '100%',
        }}
      >
        {virtualItems.map((virtualItem) => {
          const row = rows[virtualItem.index];
          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${columnsCount}, 1fr)`,
                  columnGap,
                  padding: padding,
                  boxSizing: 'border-box',
                }}
              >
                {row.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {isFetchingNextPage && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#7C3AED' }}>
          Загрузка...
        </div>
      )}
    </div>
  );
}

export default CatalogGrid;
